import { useCallback, useState } from 'react';

import { authClient } from '../lib/auth-client';
import type { PublicConfig } from '../types';
import { Turnstile } from './Turnstile';

type AuthPanelProps = {
  config: PublicConfig;
  onAuthenticated: () => Promise<void>;
};

export function AuthPanel({ config, onAuthenticated }: AuthPanelProps) {
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [registrationSent, setRegistrationSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const onCaptcha = useCallback((token: string | null) => setCaptchaToken(token), []);
  const captchaReady = !config.auth.captchaRequired || Boolean(captchaToken);
  const headers = captchaToken ? { 'x-captcha-response': captchaToken } : undefined;

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!captchaReady) return;
    setBusy(true);
    setMessage(null);

    try {
      const result =
        mode === 'register'
          ? await authClient.signUp.email({
              name,
              email,
              password,
              callbackURL: '/',
              fetchOptions: { headers },
            })
          : await authClient.signIn.email({
              email,
              password,
              callbackURL: '/',
              fetchOptions: { headers },
            });

      if (result.error) {
        setMessage(result.error.message ?? 'No fue posible completar la solicitud.');
        return;
      }

      if (mode === 'register' && config.environment !== 'local') {
        setPassword('');
        setRegistrationSent(true);
      } else {
        await onAuthenticated();
      }
    } finally {
      setBusy(false);
    }
  }

  async function social(provider: 'google' | 'microsoft') {
    if (!captchaReady) return;
    setBusy(true);
    setMessage(null);
    const result = await authClient.signIn.social({
      provider,
      callbackURL: '/',
      fetchOptions: { headers },
    });
    if (result?.error) {
      setMessage(result.error.message ?? 'No fue posible abrir el proveedor.');
      setBusy(false);
    }
  }

  async function requestReset() {
    if (!email || !captchaReady) {
      setMessage('Escribe tu correo y completa la verificación.');
      return;
    }
    setBusy(true);
    const result = await authClient.requestPasswordReset({
      email,
      redirectTo: `${window.location.origin}/restablecer`,
      fetchOptions: { headers },
    });
    setMessage(
      result.error
        ? (result.error.message ?? 'No fue posible solicitar el cambio.')
        : 'Si la cuenta existe, recibirás instrucciones por correo.',
    );
    setBusy(false);
  }

  return (
    <section id="acceso" className="glass-panel rounded-[2rem] p-6 sm:p-8">
      {registrationSent ? (
        <div role="status" className="py-4 text-center">
          <div className="mx-auto grid h-14 w-14 place-items-center rounded-full border border-emerald-300/20 bg-emerald-300/10 text-2xl text-emerald-200">
            ✓
          </div>
          <p className="eyebrow mt-6">Cuenta creada</p>
          <h2 className="mt-2 text-2xl font-semibold text-white">Revisa tu correo electrónico</h2>
          <p className="mx-auto mt-3 max-w-sm text-sm leading-6 text-zinc-400">
            Enviamos un mensaje a <strong className="text-zinc-200">{email}</strong>. Abre el enlace
            para validar la creación de tu cuenta antes de iniciar sesión.
          </p>
          <p className="mt-3 text-xs text-zinc-500">
            Si no aparece, revisa las carpetas de spam o correo no deseado.
          </p>
          <button
            type="button"
            onClick={() => {
              setRegistrationSent(false);
              setMode('login');
              setMessage(null);
            }}
            className="mt-7 rounded-full border border-white/12 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-white/5"
          >
            Ir a iniciar sesión
          </button>
        </div>
      ) : (
        <>
          <p className="eyebrow">Tu lugar en la arena</p>
          <h2 className="mt-2 text-2xl font-semibold text-white">
            {mode === 'login' ? 'Inicia sesión' : 'Crea tu cuenta'}
          </h2>
          <div className="mt-6 grid grid-cols-2 rounded-full bg-black/25 p-1 text-sm">
            {(['login', 'register'] as const).map((value) => (
              <button
                key={value}
                type="button"
                onClick={() => {
                  setMode(value);
                  setMessage(null);
                }}
                className={`rounded-full px-4 py-2.5 transition ${mode === value ? 'bg-white text-zinc-950' : 'text-zinc-400 hover:text-white'}`}
              >
                {value === 'login' ? 'Entrar' : 'Registrarme'}
              </button>
            ))}
          </div>

          {(config.auth.google || config.auth.microsoft) && (
            <div className="mt-5 grid gap-3 sm:grid-cols-2">
              {config.auth.google && (
                <SocialButton
                  label="Google"
                  disabled={busy || !captchaReady}
                  onClick={() => social('google')}
                />
              )}
              {config.auth.microsoft && (
                <SocialButton
                  label="Microsoft"
                  disabled={busy || !captchaReady}
                  onClick={() => social('microsoft')}
                />
              )}
            </div>
          )}

          <form onSubmit={submit} className="mt-5 space-y-4">
            {mode === 'register' && (
              <Field label="Alias público inicial">
                <input
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  minLength={3}
                  maxLength={40}
                  required
                  autoComplete="nickname"
                  className="field-input"
                  placeholder="Ej. RitmoAndino"
                />
              </Field>
            )}
            <Field label="Correo electrónico">
              <input
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                type="email"
                required
                autoComplete="email"
                className="field-input"
                placeholder="tu@correo.com"
              />
            </Field>
            <Field label="Contraseña">
              <input
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                type="password"
                required
                minLength={12}
                maxLength={128}
                autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                className="field-input"
                placeholder="Mínimo 12 caracteres"
              />
            </Field>

            {config.auth.captchaRequired && config.auth.turnstileSiteKey && (
              <Turnstile siteKey={config.auth.turnstileSiteKey} onToken={onCaptcha} />
            )}

            {message && (
              <p
                role="status"
                className="rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-zinc-300"
              >
                {message}
              </p>
            )}

            <button
              disabled={busy || !captchaReady}
              className="w-full rounded-full bg-white px-5 py-3 text-sm font-bold text-zinc-950 transition hover:bg-fuchsia-200 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {busy ? 'Procesando…' : mode === 'login' ? 'Entrar a la arena' : 'Crear cuenta'}
            </button>
            {mode === 'login' && (
              <button
                type="button"
                onClick={requestReset}
                className="w-full text-xs text-zinc-500 hover:text-zinc-300"
              >
                Olvidé mi contraseña
              </button>
            )}
          </form>
          {config.environment === 'local' && (
            <p className="mt-4 text-xs leading-5 text-zinc-500">
              Modo local: la cuenta se activa inmediatamente y no se envía un correo de
              verificación.
            </p>
          )}
        </>
      )}
    </section>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block text-sm text-zinc-400">
      <span className="mb-2 block">{label}</span>
      {children}
    </label>
  );
}

function SocialButton({
  label,
  disabled,
  onClick,
}: {
  label: string;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className="rounded-full border border-white/12 bg-white/5 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-white/10 disabled:opacity-50"
    >
      Continuar con {label}
    </button>
  );
}
